import { inject, injectable } from "inversify";
import { type IService, Logger } from "@designboticteam/core";
import { SceneService } from "../../stage/SceneService";
import { type ElementSceneData, fromObject } from "./ElementScene";
import { ElementSceneParser, GltfFileParser, ObjFileParser } from "./FileParser";
import { FileParserRegistry } from "./FileParserRegistry";

type ImportElementSceneOptions = {
	skipMeshRotation?: boolean;
	onProgress?: (progress: number) => void;
};

/**
 * @name AssetLoaderService
 * `Asset*` - because it is possible we will want to import other things (like e.g. documents)
 * `*Loader*` - because it is a standard convetion in THREE.js (GLTFLoader, TextureLoader, etc.)
 */
@injectable()
class AssetLoaderService implements IService {
	readonly serviceName = "📂 AssetLoaderService";
	private logger = new Logger(`🎬 WORKSPACE 3D | ${this.serviceName}`, {
		color: "#ffffff",
		backgroundColor: "#0066cc",
	});
	private readonly parserRegistry = new FileParserRegistry();

	constructor(@inject(SceneService) private sceneService: SceneService) {
		this.logger.log("constructed");
	}

	public initialize(): void {
		this.parserRegistry.register(new ObjFileParser());
		this.parserRegistry.register(new GltfFileParser());
		this.parserRegistry.register(new ElementSceneParser());

		this.logger.log("initialized");
	}

	public async importFile(file: File): Promise<string> {
		this.logger.log("Importing file:", file.name);

		const parser = this.parserRegistry.resolve(file.name);
		const object3D = await parser.parse(file);
		const sceneObjectId = this.sceneService.addObject(object3D);
		// const objectId = this.sceneService.addObject(object3D, "mesh", {
		// 	imported: true,
		// 	fileName: file.name,
		// });

		// this.cameraService.focusOnBoundingBox(object3D);

		this.logger.log("File imported:", file.name, "→", sceneObjectId);
		return sceneObjectId;
	}

	public async importElementScene(data: ElementSceneData, options?: ImportElementSceneOptions): Promise<string> {
		this.logger.log("Importing ElementScene:", data.Name);

		const group = fromObject(data, {
			skipMeshRotation: options?.skipMeshRotation,
		});

		if (!group) {
			throw new Error(`No valid meshes in ElementScene "${data.Name}"`);
		}

		const sceneObjectId = this.sceneService.addObject(group);
		// const objectId = this.sceneService.addObject(group, "group", {
		// 	imported: true,
		// 	elementScene: true,
		// 	source: data.Name,
		// });

		options?.onProgress?.(100);

		this.logger.log("ElementScene imported:", data.Name, `(${group.children.length} meshes)`, "→", sceneObjectId);

		return sceneObjectId;
	}

	public dispose() {
		this.logger.log("disposed");
	}
}

export { AssetLoaderService };
